import api, { axiosInstance } from '@/app/api';
import {create} from 'zustand';


interface AuthState {
  user: any;
  isLoading: boolean;
  error: any;
  routeAfterAuth: string;
  isAuthenticated: boolean;
  isLoginPopupOpen: boolean;
  passwordChanged?: boolean;
  getUser: () => Promise<void>;
  signUpUser: (data: any) => Promise<any>;
  loginUser: (data: any) => Promise<any>;
  logOutUser: () => Promise<boolean>;
  setRouteAfterAuth: (route: string) => Promise<void>;
  setAuthenticationStatus: (status: boolean) => Promise<void>;
  setLoginPopup: (state: boolean) => Promise<void>;
  markPasswordAsChanged: () => Promise<boolean>;
}

// Helper to clear all auth data
const clearAuthData = () => {
  // Clear localStorage
  localStorage.removeItem('access_token');
  
  // Clear sessionStorage
  sessionStorage.removeItem('session_expired');
  
  // Clear cookies - especially refresh_token
  if (typeof document !== 'undefined') {
    document.cookie.split(";").forEach(function(c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
  }
  
  // Clear axios headers
  delete axiosInstance.defaults.headers.common['Authorization'];
};

const useAuthStore = create<AuthState>((set, get) => ({
  user: {}, // Initial state for users
  isLoading: false, // To track loading state
  error: null, // To track errors,
  routeAfterAuth:'',
  isAuthenticated: false,
  isLoginPopupOpen: false,
  passwordChanged: undefined,
  getUser: async () => {
    const { isAuthenticated } = get();
    
    // Don't set loading if we're just verifying an already authenticated user
    if (!isAuthenticated) {
      set({ isLoading: true });
    }
    
    try {
      // Fetch user profile
      const response = await api.get('/users/me');
      if (!response?.data) {
        throw new Error('Network response was not ok');
      }
      
      // Additionally fetch wallet credit information
      try {
        const walletResponse = await api.get('/wallet/available-credit');
        if (walletResponse?.data) {
          // Add wallet data to user object with type assertion to avoid TypeScript errors
          const userData = response.data as any;
          const walletData = walletResponse.data as any;
          
          // Add the wallet data with fallbacks if properties are missing
          userData.wallet_limit = typeof walletData.limit === 'number' ? walletData.limit : 0;
          userData.wallet_balance = typeof walletData.balance === 'number' ? walletData.balance : 0;
          userData.available_credit = typeof walletData.available_credit === 'number' ? walletData.available_credit : 0;
        }
      } catch (walletError) {
        console.error('Error fetching wallet data:', walletError);
        // Don't fail the whole user fetch if wallet data fails
        // Make sure user still has wallet balance fields to avoid errors in UI
        const userData = response.data as any;
        userData.wallet_limit = 0;
        userData.wallet_balance = 0;
        userData.available_credit = 0;
      }
      
      const data = response.data;
      set({ user: data, isAuthenticated: true, isLoading: false, error: null });
    } catch (error: any) {
      // If unauthorized, clear auth data
      if (error?.response?.status === 401) {
        clearAuthData();
      }
      set({ error, isLoading: false, isAuthenticated: false });
      throw error; // Propagate the error to be handled by callers
    }
  },

  
  signUpUser: async (data: any) => {
    set({ isLoading: true, error: null }); // Set loading state
    try {
      const response = await api.post('/users/signup', data); // Fetch users from an API
      set({ isLoading: false, error: null });
      return response;
    } catch (error: any) {
      set({ error, isLoading: false }); // Handle errors
      throw error;
    }
  },
  loginUser: async (data: any) => {
    set({ isLoading: true, error: null });
    
    try {
      // Clear any previous auth data before login
      clearAuthData();
      
      const response: any = await api.post('/auth/login', data, {
        withCredentials: true
      });
      
      if (response.data?.access_token) {
        // Store token
        localStorage.setItem('access_token', response.data.access_token);
        
        // Set the token in axios defaults
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        
        // Check password changed status from response
        const passwordChanged = response.data.password_changed;
        
        // Update auth state
        set({ 
          isLoading: false, 
          error: null, 
          isAuthenticated: true,
          user: response.data.user || {},
          passwordChanged
        });
        
        return response;
      } else {
        throw new Error('Login failed: No access token received');
      }
    } catch (error) {
      console.error("Login error:", error); // Debug log
      set({ error, isLoading: false, isAuthenticated: false });
      throw error;
    }
  },
  logOutUser: async () => {
    // Immediately set loading state and save current authentication state
    const wasAuthenticated = get().isAuthenticated;
    set({ isLoading: true, error: null });
    
    try {
      // Clear all auth data first
      clearAuthData();
      
      // Update state immediately
      set({ 
        user: {}, 
        isAuthenticated: false, 
        isLoading: false, 
        error: null,
        passwordChanged: undefined
      });
      
      // Only make API call if previously authenticated
      if (wasAuthenticated) {
        try {
          // Attempt to clear server-side session
          await api.post('/users/logout', {}, {
            withCredentials: true
          });
          
          // If success, toast message
          // toast.success('Logged out successfully');
        } catch (err) {
          // Log error but continue with local logout
          console.error('Error during logout API call:', err);
        }finally{
          localStorage.removeItem('access_token');
          sessionStorage.removeItem('session_expired');
          window.location.href = '/login';
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error during logout:', error);
      
      // Even if there's an error, ensure all data is cleared
      clearAuthData();
      
      set({ 
        user: {}, 
        isAuthenticated: false, 
        isLoading: false, 
        error: null,
        passwordChanged: undefined
      });
      
      return false;
    }
  },
  setRouteAfterAuth: async (route: string) => {
    set({ routeAfterAuth: route });
  },
  setAuthenticationStatus: async (status: boolean) => {
    set({ isAuthenticated: status });
    
    // If setting to false, ensure we clear all auth state
    if (!status) {
      clearAuthData();
      set({
        user: {},
        passwordChanged: undefined
      });
    }
  },
  setLoginPopup: async (state: boolean) => {
    set({ isLoginPopupOpen: state });
  },
  markPasswordAsChanged: async () => {
    // Set the flag in the store immediately
    set({ passwordChanged: true });
    
    // Update the backend
    try {
      await api.patch('/users/mark-password-changed', {});
      return true;
    } catch (error) {
      console.error('Failed to mark password as changed:', error);
      // Don't revert the UI state to avoid confusion
      return false;
    }
  }
}));

export default useAuthStore;